import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      loadUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const loadUser = async () => {
    try {
      const response = await axios.get('/auth/me');
      setUser(response.data.user);
    } catch (error) {
      console.error('Failed to load user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password, totpCode = null) => {
    try {
      const response = await axios.post('/auth/login', { email, password, totpCode });
      const data = response.data;

      if (data.requires_2fa) {
        return { success: false, requires_2fa: true };
      }

      if (data.requires_2fa_setup) {
        return { success: false, requires_2fa_setup: true, temp_token: data.temp_token };
      }

      const { token: newToken, user: userData } = data;
      localStorage.setItem('token', newToken);
      setToken(newToken);
      setUser(userData);
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Login failed'
      };
    }
  };

  const loginWithToken = (newToken, userData) => {
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
  };

  const isAdmin = () => user?.role === 'admin';
  const isMediaBuyer = () => user?.role === 'media_buyer';
  const isManager = () => user?.role === 'manager';
  const isPartner = () => user?.role === 'partner';
  const canEdit = () => isAdmin() || isManager();

  return (
    <AuthContext.Provider value={{
      user, token, loading, login, loginWithToken, logout,
      isAuthenticated: !!user,
      isAdmin, isManager, isMediaBuyer, isPartner, canEdit
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
